import { useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getFormattedDate } from 'lib/common';
import {
  Pagination,
  ProductLinkWithIcon,
  TimeIndicator,
  User
} from 'components/common';
import {
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableRow
} from 'components/common/box';

import type { GetTeamActivitiesRes } from 'shared-types/responses';

interface Props {
  activities: GetTeamActivitiesRes['activities'];
  colorsByActivityType: { [category: string]: string };
  loading: boolean;
}

const MyTeamActivityList = ({ activities, colorsByActivityType }: Props) => {
  const { language, messages, theme } = useReduxState();

  const [page, setPage] = useState(1);

  const activitiesPerPage = 10;

  const selectedActivities =
    activities?.slice(
      (page - 1) * activitiesPerPage,
      page * activitiesPerPage
    ) || [];

  const pages = Math.ceil((activities?.length || 0) / activitiesPerPage);

  return (
    <div className="activity-list">
      {activities?.length ? (
        <div className="activities">
          <BoxOverflowWrapper>
            <BoxTable>
              {selectedActivities.map((activity, index) => (
                <BoxTableRow key={`${activity.type}-${activity.id}-${index}`}>
                  <BoxTableCell>
                    <div className="user">
                      <User enableProfileLink={true} user={activity.user} />
                    </div>
                  </BoxTableCell>
                  <BoxTableCell>
                    <div className="type">
                      <div
                        className="type-icon"
                        style={{
                          backgroundColor: colorsByActivityType[activity.type]
                        }}
                      />
                      {
                        {
                          achievement: {
                            conversation:
                              messages.achievementTypeConversationTimeline,
                            external: messages.achievementTypeExternalTimeline,
                            'group-conversation':
                              messages.achievementTypeGroupConversationTimeline,
                            milestone:
                              messages.achievementTypeMilestoneTimeline,
                            tasklist: messages.achievementTypeTasklistTimeline
                          }[
                            activity.type === 'achievement'
                              ? activity.achievement?.type
                              : ''
                          ],
                          competency: messages.notificationsTextNewCompetencies,
                          course: messages.profileTimelineCourseCompleted,
                          'calendar-event': messages.eventCalendarEvent,
                          'individual-achievement':
                            messages.individualAchievement,
                          path: messages.profileTimelineLearningPathCompleted
                        }[activity.type]
                      }
                    </div>
                  </BoxTableCell>
                  <BoxTableCell>
                    <ProductLinkWithIcon
                      accessType="supervisor"
                      userId={activity.user.id}
                      {...(activity.type === 'achievement'
                        ? { achievement: activity.achievement }
                        : activity.type === 'calendar-event'
                        ? { calendarEvent: activity.calendarEvent }
                        : activity.type === 'competency'
                        ? { competency: activity.competency }
                        : activity.type === 'course'
                        ? { course: activity.course }
                        : activity.type === 'individual-achievement'
                        ? { individualAchievement: activity }
                        : activity.type === 'path'
                        ? { path: activity.path }
                        : {})}
                    />
                  </BoxTableCell>
                  <BoxTableCell shrink={true}>
                    <div
                      aria-label={getFormattedDate({
                        date: activity.completionDate
                      })}
                      data-tooltip={getFormattedDate({
                        date: activity.completionDate
                      })}
                      className="date"
                    >
                      <TimeIndicator
                        language={language}
                        style={{ whiteSpace: 'nowrap' }}
                        timeString={activity.completionDate as string}
                      />
                    </div>
                  </BoxTableCell>
                </BoxTableRow>
              ))}
            </BoxTable>
          </BoxOverflowWrapper>
        </div>
      ) : null}

      <Pagination
        currentPage={page}
        numberOfPages={pages}
        onPageChange={({ page }) => setPage(page)}
        style={{ margin: '15px 0' }}
      />

      <style jsx>{`
        .user {
          min-width: 160px;
        }
        .type {
          color: ${theme.textColorLighter};
          font-size: 1.5rem;
          display: flex;
        }
        .type-icon {
          border-radius: 20px;
          flex-shrink: 0;
          margin-right: 7px;
          margin-top: 4px;
          height: 11px;
          width: 11px;
        }
        .date {
          color: ${theme.textColorLighter};
        }
        .activities {
          border-bottom: 1px solid ${theme.lightGrey};
          border-top: 1px solid ${theme.lightGrey};
        }
        @media (max-width: ${breakpoints.large - 1}px) {
          .user {
            margin-top: 10px;
          }
          .date {
            margin-bottom: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default MyTeamActivityList;
